import * as THREE from 'three';
import { BufferGeometry, Vector2, Vector3 } from 'three';
import { TrackballControls } from 'three/examples/jsm/controls/TrackballControls';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { Viewer } from './Viewer';
import  HSBControls  from './Camera/HSBControls';
import { ViewerDisplayDirection } from '../hsb-viewerdisplaydirection';

export enum Sides {
    Front = 1 << 0,
    Back = 1 << 1,
    Left = 1 << 2,
    Right = 1 << 3,
    Top = 1 << 4,
    Bottom = 1 << 5,
}

export class ViewCube {
    _renderer!: THREE.WebGLRenderer;
    _viewer: Viewer;
    _container: HTMLElement | null = null;
    _localCamera!: THREE.Camera;
    _localControls!: OrbitControls;
    _refCamera: THREE.PerspectiveCamera | THREE.OrthographicCamera | null = null;
    _refControls: TrackballControls | OrbitControls | HSBControls | null = null;
    _cubeMesh?: THREE.Mesh;
    _scene: THREE.Scene | null = null;
    _raycaster: THREE.Raycaster | null = null;
    _layer?: THREE.Layers;
    _mouse: THREE.Vector2 | null = null;
    _dragDelta?: Vector2;
    _isDragging?: boolean;

    _chamfer: number = 0.3;
    _color: number = 0xffffff;
    _edgeColor: number = 0x909090;
    _highlightColor: number = 0xddb372;

    _width: number;
    _height: number;
    _radius: number = 3;

    _hovered?: THREE.Mesh;
    _tempMaterial: any;

    _tweenStart?: Vector3;
    _tweenTarget?: Vector3;
    _tweenFocus?: Vector3;
    _tweenDistance?: number;
    _tweenTime: number = 5;
    _tween?: number;
    _viewPresets: any = {};
    private _active: boolean = false;
    private _renderOperation: NodeJS.Timeout | null = null;

    private increaseListener: ((...args: any[]) => any) | null = null;
    private decreaseListener: ((...args: any[]) => any) | null = null;
    private onDragListener: ((...args: any[]) => any) | null = null;
    private conditionalIncreaseListener: ((...args: any[]) => any) | null = null;

    public set RefCamera(camera: THREE.PerspectiveCamera | THREE.OrthographicCamera | null) {
        this._refCamera = camera;
        this.updateCamera();
    }

    public get RefCamera(): THREE.PerspectiveCamera | THREE.OrthographicCamera | null {
        return this._refCamera;
    }

    public set RefControls(controls: TrackballControls | OrbitControls | HSBControls | null) {
        this._refControls = controls;
        this.updateCamera();
    }
    public get RefControls() {
        return this._refControls;
    }

    constructor(
        viewer: Viewer,
        refCamera: THREE.PerspectiveCamera | THREE.OrthographicCamera,
        container: HTMLElement,
        width: number,
        height: number
    ) {
        if (!viewer || !refCamera || !container)
            throw new Error('new ViewCube(): passed parameters can not be null!');
        this._viewer = viewer;
        this._refCamera = refCamera;
        this._refControls = viewer.currentControls;
        this._container = container;
        this._width = width;
        this._height = height;

        this._raycaster = new THREE.Raycaster();

        this.createRenderer();
        this.createScene();
        this.createCamera();
        this.registerMouseEvent();

        this._container.style.transition = 'width 0.15s linear, height 0.15s linear';
        this.increaseListener = this.increaseSize.bind(this);
        this.decreaseListener = this.decreaseSize.bind(this);
        this.onDragListener = this.onDrag.bind(this);
        this.conditionalIncreaseListener = this.conditionalIncrease.bind(this);

        this._renderer.domElement.removeEventListener('pointerenter', this.increaseListener);
        this._renderer.domElement.addEventListener('pointerenter', this.increaseListener, {
            once: true,
        });
    }

    /**
     * Disposes the viewcibe
     */
    public dispose() {
        this.stop();
        let disposal = (object: any) => {
            object.texture?.dispose();
            object.geometry?.dispose();
        };
        if (this._scene) {
            disposal(this._scene);
            this._scene.remove(...this._scene.children);
            this._scene = null;
        }

        this._raycaster = null;
        this._refCamera = null;
        this._refControls = null;

        if (this._container) {
            this._container.onresize = null;
            this.unregisterMouseEvent();
            this._container.remove();
            this._container = null;
        }

        if (this._localControls) {
            this._localControls.removeEventListener('start', this.onStart);
            this._localControls.removeEventListener('change', this.onChange);
            this._localControls.removeEventListener('end', this.onEnd);
        }
    }

    /**
     * Creates the renderer for the viewcube
     */
    createRenderer() {
        this._renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        this._renderer.setClearColor(0, 0);
        this._renderer.setSize(this._width * 2, this._height * 2, true);
        this._renderer.domElement.style.width = '100%';
        this._renderer.domElement.style.height = '100%';
        this._container!.appendChild(this._renderer.domElement);
    }

    /**
     * Creates the scene for the viewcube
     */
    createScene() {
        this._cubeMesh = new THREE.Mesh();
        this.createMainFacets();
        this.createEdgeFacets();
        this.createCornerFacets();
        this.createLabels();

        this._scene = new THREE.Scene();
        this._scene.add(this._cubeMesh);
        const hlight = new THREE.AmbientLight(0x404040, 100);
        this._scene.add(hlight);
    }

    getViewNormal(geometry: THREE.BufferGeometry) {
        let normals = geometry.getAttribute('normal');

        // Get first face normal by baveraging first 3 normals
        let averageNormal = [0, 1, 2].reduce((accu, curr) => {
            let currVector = new THREE.Vector3(
                normals.getX(curr),
                normals.getY(curr),
                normals.getZ(curr)
            );
            accu.add(currVector);
            return accu;
        }, new THREE.Vector3());
        return averageNormal.normalize();
    }

    /**
     * Creates the main faces for the cube
     */
    createMainFacets() {
        // it's math: the projection of the champer on the plane is champer / sqrt(2)
        // (Pythagoras), reduce from both side and you get:
        let width = 1.0 - Math.sqrt(2) * this._chamfer;
        let plane = new THREE.PlaneGeometry(width, width).translate(0, 0, 0.5);
        let halfPi = Math.PI / 2;
        let geoms: THREE.BufferGeometry[] = [];

        geoms[Sides.Bottom] = plane.clone().rotateX(halfPi);

        this._viewPresets[ViewerDisplayDirection.Bottom] = this.getViewNormal(geoms[Sides.Bottom]);

        geoms[Sides.Top] = plane.clone().rotateX(-halfPi).rotateY(Math.PI);
        this._viewPresets[ViewerDisplayDirection.Top] = this.getViewNormal(geoms[Sides.Top]);

        geoms[Sides.Left] = plane.clone().rotateY(-halfPi);
        this._viewPresets[ViewerDisplayDirection.Left] = this.getViewNormal(geoms[Sides.Left]);

        geoms[Sides.Right] = plane.clone().rotateY(halfPi);
        this._viewPresets[ViewerDisplayDirection.Right] = this.getViewNormal(geoms[Sides.Right]);

        geoms[Sides.Front] = plane.clone();
        this._viewPresets[ViewerDisplayDirection.Front] = this.getViewNormal(geoms[Sides.Front]);

        geoms[Sides.Back] = plane.clone().rotateX(-Math.PI).rotateZ(Math.PI);
        this._viewPresets[ViewerDisplayDirection.Back] = this.getViewNormal(geoms[Sides.Back]);

        geoms.forEach((geom, i) => {
            let mesh = new THREE.Mesh(geom, new THREE.MeshBasicMaterial());
            mesh.userData.sides = i;
            mesh.userData.type = 'face';
            this._cubeMesh?.add(mesh);
        });
    }

    /**
     * Creates the faces on the sides
     */
    createEdgeFacets() {
        // it's math: the projection of the champer on the plane is champer / sqrt(2)
        // (Pythagoras), reduce from both side and you get:
        let width = this._chamfer;
        let height = 1.0 - Math.sqrt(2) * this._chamfer;
        let plane = new THREE.PlaneGeometry(width, height);
        let mat = new THREE.MeshBasicMaterial({
            color: this._color,
        });
        let piBy2: number = Math.PI / 2; // 90 degrees
        let piBy4: number = Math.PI / 4; // 45 degrees
        let geoms: BufferGeometry[] = [];
        let offset: number = Math.sqrt(2) / 2 - this._chamfer / 2;
        plane.translate(0, 0, offset);

        // side edges
        geoms[Sides.Bottom | Sides.Left] = plane.clone().rotateX(piBy2).rotateZ(piBy4);
        geoms[Sides.Left | Sides.Top] = geoms[Sides.Bottom | Sides.Left].clone().rotateZ(piBy2);
        geoms[Sides.Top | Sides.Right] = geoms[Sides.Left | Sides.Top].clone().rotateZ(piBy2);
        geoms[Sides.Right | Sides.Bottom] = geoms[Sides.Top | Sides.Right].clone().rotateZ(piBy2);

        // top edges
        geoms[Sides.Back | Sides.Left] = plane.clone().rotateY(piBy4);
        geoms[Sides.Back | Sides.Top] = geoms[Sides.Back | Sides.Left].clone().rotateZ(piBy2);
        geoms[Sides.Back | Sides.Right] = geoms[Sides.Back | Sides.Top].clone().rotateZ(piBy2);
        geoms[Sides.Back | Sides.Bottom] = geoms[Sides.Back | Sides.Right].clone().rotateZ(piBy2);

        // botom edges
        geoms[Sides.Front | Sides.Left] = plane.clone().rotateY(piBy4 + piBy2);
        geoms[Sides.Front | Sides.Top] = geoms[Sides.Front | Sides.Left].clone().rotateZ(piBy2);
        geoms[Sides.Front | Sides.Right] = geoms[Sides.Front | Sides.Top].clone().rotateZ(piBy2);
        geoms[Sides.Bottom | Sides.Front] = geoms[Sides.Front | Sides.Right].clone().rotateZ(piBy2);

        geoms.forEach((geom: THREE.BufferGeometry, i) => {
            let sideMat = mat.clone();
            let mesh = new THREE.Mesh(geom, sideMat);
            mesh.userData.sides = i;
            mesh.userData.type = 'edge';
            this._cubeMesh?.add(mesh);

            // create wireframe
            let attr = geom.getAttribute('position');
            let pts: THREE.Vector3[] = [];
            for (let index = 0; index < attr.count; index++) {
                pts.push(new THREE.Vector3(attr.getX(index), attr.getY(index), attr.getZ(index)));
            }

            let border = new THREE.BufferGeometry().setFromPoints([
                pts[0],
                pts[1],
                pts[3],
                pts[2],
                pts[0],
            ]);

            const lineMat = new THREE.LineBasicMaterial({ color: this._edgeColor }); // TODO make param
            var line = new THREE.Line(border, lineMat);
            mesh.add(line); // the hierarchy is important for ray casting
        });
    }
    //#region Zoom on hover
    private increaseSize(e: Event) {
        try {
            if (!this._container || !this._renderer) return;
            e.stopPropagation();
            if ((e as PointerEvent)?.pointerType === 'touch') {
                this._container.style.width = 2 * this._width + 'px';
                this._container.style.height = 2 * this._height + 'px';
                setTimeout(() => {
                    if (this.conditionalIncreaseListener)
                        document.addEventListener('pointerdown', this.conditionalIncreaseListener!);
                }, 500);
            } else {
                if (this.onDragListener)
                    this._localControls.addEventListener('start', this.onDragListener!);
                if (this.decreaseListener) {
                    this._renderer.domElement.removeEventListener(
                        'pointerleave',
                        this.decreaseListener
                    );
                    this._renderer.domElement.addEventListener(
                        'pointerleave',
                        this.decreaseListener
                    );
                }

                this._container.style.width = 2 * this._width + 'px';
                this._container.style.height = 2 * this._height + 'px';
            }
        } catch (err) {
            console.error('ViewCube.increaseSize(): Something went wrong', err);
        }
    }

    private onDrag(e: Event) {
        if (this.onDragListener)
            this._localControls.removeEventListener('start', this.onDragListener);
        if (!this.decreaseListener) return;
        this._renderer.domElement.removeEventListener('pointerleave', this.decreaseListener);
        this._localControls.removeEventListener('end', this.decreaseListener);
        this._localControls.addEventListener('end', this.decreaseListener);
    }

    private conditionalIncrease(e: Event) {
        e.stopPropagation();
        if ((e.target as any).parentElement.id.toLowerCase() !== 'viewcube') {
            if (this.conditionalIncreaseListener)
                document.removeEventListener('pointerdown', this.conditionalIncreaseListener);
            this.decreaseSize(e);
        }
    }

    private decreaseSize(e: Event) {
        e.stopPropagation?.();
        if (this.decreaseListener) {
            this._renderer.domElement.removeEventListener('pointerleave', this.decreaseListener);
            this._localControls.removeEventListener('end', this.decreaseListener);
        }

        if (this._container) {
            this._container.style.width = this._width + 'px';
            this._container.style.height = this._height + 'px';
        }

        if ((e as PointerEvent)?.pointerType === 'touch') {
            if (this.increaseListener)
                document.removeEventListener('pointerdown', this.increaseListener);
            setTimeout(() => {
                if (this.increaseListener) {
                    this._renderer.domElement.removeEventListener(
                        'pointerenter',
                        this.increaseListener
                    );
                    this._renderer.domElement.addEventListener(
                        'pointerenter',
                        this.increaseListener,
                        { once: true }
                    );
                }
            }, 500);
        } else if (this.increaseListener) {
            this._renderer.domElement.removeEventListener('pointerenter', this.increaseListener);
            this._renderer.domElement.addEventListener('pointerenter', this.increaseListener, {
                once: true,
            });
        }
    }
    //#endregion

    //#region Render loop
    public start() {
        this._active = true;
        if (!this._renderOperation) this.render();
    }

    public stop() {
        this._active = false;
        if (this._renderOperation) {
            clearTimeout(this._renderOperation);
            this._renderOperation = null;
        }
    }

    /**
     * Renders the viewcube
     */
    private render() {
        if (!this._active) return;

        let userQuat = this._refCamera?.quaternion;
        let localQuat = this._localCamera?.quaternion;
        if (!userQuat || !localQuat) return;
        let changed = !userQuat.equals(localQuat);

        if (changed) {
            this.updateCamera();
        }
        if (this._scene && this._localCamera)
            this._renderer?.render(this._scene, this._localCamera);
        this._renderOperation = setTimeout(() => {
            requestAnimationFrame(this.render.bind(this));
        }, 1000 / 60);
    }

    /**
     * Renders the viewcube
     */
    public renderFrame() {
        let userQuat = this._refCamera?.quaternion;
        let localQuat = this._localCamera?.quaternion;
        let changed = localQuat && !userQuat?.equals(localQuat);

        if (changed) {
            this.updateCamera();
        }
        if (this._scene && this._localCamera)
            this._renderer?.render(this._scene, this._localCamera);
    }

    //#endregion

    //#region  helpers
    /**
     * Returns the closest vertex of a mesh for the given point.
     * @param mesh The mesh from which to retrieve the closest vertex
     * @param vec The point for which to get the closest vertex.
     */
    getClosesVertexOfPlaneMesh(mesh: THREE.Mesh, vec: Vector3): Vector3 {
        let geom: THREE.PlaneGeometry = mesh.geometry as THREE.PlaneGeometry;
        let attr = geom.getAttribute('position');
        let closest: Vector3 = new Vector3(attr.getX(0), attr.getY(0), attr.getZ(0));
        // distancetosquared is more performant than distanceto when comparing distances (no sqrt needed).
        let bestDist = closest.distanceToSquared(vec);
        for (let i = 1; i < attr.count; i++) {
            let testVec: Vector3 = new Vector3(attr.getX(i), attr.getY(i), attr.getZ(i));
            let dist = testVec.distanceToSquared(vec);
            if (dist < bestDist) {
                bestDist = dist;
                closest = testVec;
            }
        }
        return closest;
    }

    /**
     * Returns the mesh of the selected side
     * @param side The side that was clicked
     */
    getMeshOfSide(side: Sides): THREE.Mesh {
        return this._cubeMesh?.children.find((m) => m.userData.sides === side) as THREE.Mesh;
    }

    /**
     * Returns THREE.triangle between the 3 meshes for teh given point
     * @param a mesh 1
     * @param b mesh 2
     * @param c mesh 3
     * @param corner Point for which to check the closest point fo each mesh.
     */
    getTriangleOfSides(a: Sides, b: Sides, c: Sides, corner: Vector3): THREE.Triangle {
        return new THREE.Triangle(
            this.getClosesVertexOfPlaneMesh(this.getMeshOfSide(a), corner),
            this.getClosesVertexOfPlaneMesh(this.getMeshOfSide(b), corner),
            this.getClosesVertexOfPlaneMesh(this.getMeshOfSide(c), corner)
        );
    }

    /**
     * Returns THREE.mesh between the 3 given meshes for the given corner
     * @param a mesh 1
     * @param b mesh 2
     * @param c  mesh 3
     * @param corner Point for which to check the closest point fo each mesh.
     */
    createCornerMesh(a: Sides, b: Sides, c: Sides, corner: Vector3): THREE.Mesh {
        let geom = new BufferGeometry();
        let triangle = this.getTriangleOfSides(a, b, c, corner);
        geom.setFromPoints([triangle.a, triangle.b, triangle.c]);
        geom.computeVertexNormals();
        let mat = new THREE.MeshBasicMaterial({
            color: this._color,
        });
        let mesh = new THREE.Mesh(geom, mat);
        mesh.userData.sides = a | b | c;
        mesh.userData.type = 'vertex';

        let sides = a + b + c;

        if (sides == (Sides.Top | Sides.Left | Sides.Front)) {
            this._viewPresets[ViewerDisplayDirection.SouthWest] = this.getViewNormal(mesh.geometry);
        } else if (sides == (Sides.Top | Sides.Back | Sides.Left)) {
            this._viewPresets[ViewerDisplayDirection.NorthWest] = this.getViewNormal(mesh.geometry);
        } else if (sides == (Sides.Top | Sides.Front | Sides.Right)) {
            this._viewPresets[ViewerDisplayDirection.SouthEast] = this.getViewNormal(mesh.geometry);
        } else if (sides == (Sides.Top | Sides.Right | Sides.Back)) {
            this._viewPresets[ViewerDisplayDirection.NorthEast] = this.getViewNormal(mesh.geometry);
        }

        return mesh;
    }
    /**
     * Returns requested corner pt of the input side
     * @param side
     * @param corner required
     */
    getCornerPt(side, corner) {
        let vecY = new Vector3(0, 1, 0);
        let mesh = this.getMeshOfSide(side);
        let vecZ = this.getViewNormal(mesh.geometry).negate();
        let vecX = vecZ.clone().cross(vecY);
        mesh.geometry.computeBoundingBox();
        let bBox = mesh.geometry.boundingBox;
        if (!bBox) return new THREE.Vector3();

        let attr = mesh.geometry.getAttribute('position');
        let pts: THREE.Vector3[] = [];
        for (let index = 0; index < 3; index++) {
            pts.push(new THREE.Vector3(attr.getX(index), attr.getY(index), attr.getZ(index)));
        }

        let vecLength = pts[1].clone().sub(pts[0]);
        length = Math.abs(vecX.dot(vecLength)) / 2;
        let cornerPt = bBox.getCenter(new Vector3());

        if (corner == (Sides.Top | Sides.Left)) {
            cornerPt
                .add(vecX.clone().multiplyScalar(-length))
                .add(vecY.clone().multiplyScalar(length));
        } else if (corner == (Sides.Top | Sides.Right)) {
            cornerPt
                .add(vecX.clone().multiplyScalar(length))
                .add(vecY.clone().multiplyScalar(length));
        } else if (corner == (Sides.Bottom | Sides.Left)) {
            cornerPt
                .add(vecX.clone().multiplyScalar(-length))
                .add(vecY.clone().multiplyScalar(-length));
        } else if (corner == (Sides.Bottom | Sides.Right)) {
            cornerPt
                .add(vecX.clone().multiplyScalar(length))
                .add(vecY.clone().multiplyScalar(-length));
        }

        return cornerPt;
    }

    /**
     * Creates meshes for each corner
     */
    createCornerFacets() {
        if (!this._cubeMesh) return;
        this._cubeMesh.add(
            this.createCornerMesh(
                Sides.Left,
                Sides.Front,
                Sides.Top,
                this.getCornerPt(Sides.Front, Sides.Top | Sides.Left)
            )
        );
        this._cubeMesh.add(
            this.createCornerMesh(
                Sides.Front,
                Sides.Right,
                Sides.Top,
                this.getCornerPt(Sides.Front, Sides.Top | Sides.Right)
            )
        );
        this._cubeMesh.add(
            this.createCornerMesh(
                Sides.Right,
                Sides.Back,
                Sides.Top,
                this.getCornerPt(Sides.Back, Sides.Top | Sides.Left)
            )
        );
        this._cubeMesh.add(
            this.createCornerMesh(
                Sides.Back,
                Sides.Left,
                Sides.Top,
                this.getCornerPt(Sides.Back, Sides.Top | Sides.Right)
            )
        );

        this._cubeMesh.add(
            this.createCornerMesh(
                Sides.Front,
                Sides.Left,
                Sides.Bottom,
                this.getCornerPt(Sides.Front, Sides.Bottom | Sides.Left)
            )
        );
        this._cubeMesh.add(
            this.createCornerMesh(
                Sides.Right,
                Sides.Front,
                Sides.Bottom,
                this.getCornerPt(Sides.Front, Sides.Bottom | Sides.Right)
            )
        );
        this._cubeMesh.add(
            this.createCornerMesh(
                Sides.Back,
                Sides.Right,
                Sides.Bottom,
                this.getCornerPt(Sides.Back, Sides.Bottom | Sides.Left)
            )
        );
        this._cubeMesh.add(
            this.createCornerMesh(
                Sides.Left,
                Sides.Back,
                Sides.Bottom,
                this.getCornerPt(Sides.Back, Sides.Bottom | Sides.Right)
            )
        );
    }

    /**
     * Creates the local camera for the viewcube
     */
    createCamera() {
        let ratio = this._width / this._height;
        this._localCamera = new THREE.PerspectiveCamera(45, ratio, 0.01, 5);
        if (this._refCamera) this._localCamera.up.copy(this._refCamera.up);
        this._localCamera.position.copy(this._localCamera.up.clone().multiplyScalar(-this._radius));
        this._localCamera.lookAt(this._cubeMesh?.position ?? new THREE.Vector3());
        this._localCamera.updateMatrix();
        if (!this._container) return;
        this._localControls = new OrbitControls(this._localCamera, this._container);
        this._localControls.mouseButtons = {
            LEFT: THREE.MOUSE.ROTATE,
            MIDDLE: THREE.MOUSE.PAN,
            RIGHT: THREE.MOUSE.DOLLY,
        };
        this._localControls.enableZoom = false;
        this._localControls.enablePan = false;
        this._localControls.rotateSpeed = 0.1;
        this._localControls.addEventListener('start', this.onStart.bind(this));
        this._localControls.addEventListener('change', this.onChange.bind(this));
        this._localControls.addEventListener('end', this.onEnd.bind(this));
        this._localControls.enableDamping = false;
        this._localControls.dampingFactor = 0.2;
        this._localControls.maxPolarAngle = Math.PI - 0.001;

        this._scene?.add(this._localCamera);
    }
    //#endregion

    //#region events

    onStart() {
        this._isDragging = true;
        this._dragDelta = new THREE.Vector2(0, 0);
    }

    onChange(e) {
        if (!this._isDragging || !this._localCamera) return;
        let dir = this._localCamera.getWorldDirection(new THREE.Vector3());
        // Need te reverse direction to rotate
        dir = dir.multiplyScalar(-1);
        this._viewer.cameraUtils?.FocusCameraInDirection({ direction: dir });
    }
    onEnd() {
        this._isDragging = false;
    }

    /**
     * Registers the on click event
     */
    registerMouseEvent() {
        if (!this._container) return;
        this._container.onclick = (event: MouseEvent) => {
            if (this._isDragging) {
                this._isDragging = false;
                if (!this._dragDelta) this._dragDelta = new THREE.Vector2();
                if (this._dragDelta.lengthSq() <= 24) {
                    this.onClick(event);
                }
                this._dragDelta = new Vector2();
            } else {
                this.onClick(event);
            }
        };

        this._container.onmousemove = (event: MouseEvent) => {
            this.onMouseMove(event);
            if (this._isDragging && this._dragDelta) {
                this._dragDelta.x += event.movementX;
                this._dragDelta.y += event.movementY;
            }
        };

        this._container.onmouseleave = (event: MouseEvent) => {
            this._mouse = null;
        };

        this._container.onmouseenter = (event: MouseEvent) => {
            if (!this._container) return;
            this._mouse = new THREE.Vector2(
                +(event.offsetX / this._container.clientWidth) * 2 - 1,
                -(event.offsetY / this._container.clientHeight) * 2 + 1
            );
        };
    }

    /**
     * Registers the on click event
     */
    unregisterMouseEvent() {
        if (!this._container) return;
        this._container.onclick = null;
        this._container.onmousemove = null;
        this._container.onmouseleave = null;
        this._container.onmouseenter = null;
    }

    /**
     * Performs a raycast whenever this canvas is clicked
     * @param event
     */
    private onClick(event: MouseEvent) {
        if (this._cubeMesh) {
            if (!this._container || !this._raycaster) return;
            this._mouse = new THREE.Vector2(
                +(event.offsetX / this._container.clientWidth) * 2 - 1,
                -(event.offsetY / this._container.clientHeight) * 2 + 1
            );

            this._raycaster.setFromCamera(this._mouse, this._localCamera);
            let intersects = this._raycaster.intersectObjects(this._cubeMesh.children, false);
            if (intersects && intersects.length > 0) {
                if (intersects[0].object.userData.sides) {
                    this.onSideCliked(
                        intersects[0].face?.normal ?? new THREE.Vector3(0, 1, 0),
                        true
                    );
                    return;
                }
            }
        }
    }

    public HandleResize() {
        if (!this._container) return;
        this._renderer.setSize(this._container.clientWidth, this._container.clientHeight);
    }
    //#endregion

    /**
     * Updates the local camera to reflect the referenced camera
     */
    updateCamera() {
        if (!this._localCamera || !this._refCamera) return;
        if (!this._isDragging) {
            let cameraForward: Vector3 = new Vector3();
            this._refCamera.getWorldDirection(cameraForward);
            let targetPos = cameraForward.multiplyScalar(-this._radius);
            this._localCamera.up.copy(this._refCamera.up);
            this._localCamera.position.copy(targetPos);

            this._localCamera.lookAt(this._cubeMesh?.position ?? new THREE.Vector3());
            this._localCamera.updateMatrix();
            this._localControls.update();
        }
        this.HighlightHover();
    }

    /**
     * Creates labels for each main face
     */
    createLabels() {
        let sides = [Sides.Front, Sides.Back, Sides.Left, Sides.Right, Sides.Top, Sides.Bottom];
        let canvasSize = 256; // textures need 2^N, N=7
        let fontSize: number = 72;

        // find common font size
        let longestString = Sides[Sides.Bottom];
        let canvas = document.createElement('canvas');
        canvas.width = canvasSize;
        canvas.height = canvasSize;
        let ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.font = `bold ${fontSize}px Monospace`;
        let pixels = ctx.measureText(longestString);
        let ratio = canvasSize / pixels.width;
        fontSize = Math.round(fontSize * ratio * 0.9); // 90% for padding

        for (let i in sides) {
            let side = sides[i];
            let str = Sides[side];

            let canvas = document.createElement('canvas');
            canvas.width = canvasSize;
            canvas.height = canvasSize;
            let ctx = canvas.getContext('2d');
            if (!ctx) return;
            ctx.fillStyle = `#${this._color.toString(16)}`;
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            ctx.font = `bold ${fontSize}px Monospace`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillStyle = 'black';
            ctx.fillText(str.toLocaleUpperCase(), canvas.width / 2, canvas.height / 2);

            let mesh = this.getMeshOfSide(side);
            let mat = mesh.material as THREE.MeshBasicMaterial;
            mat.map = new THREE.CanvasTexture(canvas);

            if (side == Sides.Top) {
                mat.map.center = new THREE.Vector2(0.5, 0.5);
                mat.map.rotation = Math.PI;
            }
        }
    }

    /**
     * Set the view to a viewPreset
     * @param view preset
     */
    public setPresetView(viewPreset: ViewerDisplayDirection, tween: boolean) {
        let normal = this._viewPresets[viewPreset];
        this.onSideCliked(normal, tween);
    }

    /**
     * Starts up the rotation tween whenever a side is clicked
     * @param mesh
     * @param normal
     */
    public onSideCliked(normal: Vector3, tween: boolean) {
        var rNormal = new THREE.Vector3(
            Math.round(normal.x * 100) / 100,
            Math.round(normal.y * 100) / 100,
            Math.round(normal.z * 100) / 100
        );
        let dot = rNormal.dot(new THREE.Vector3(0, 1, 0));
        if (Math.abs(dot) > 0.99) {
            rNormal.z += 0.00001;
        }
        this._viewer.cameraUtils?.FocusCameraInDirection({ direction: rNormal, tween: true });
    }
    onMouseMove(event: MouseEvent) {
        if (!this._container) return;
        this._mouse = new THREE.Vector2(
            +(event.offsetX / this._container.clientWidth) * 2 - 1,
            -(event.offsetY / this._container.clientHeight) * 2 + 1
        );

        this.HighlightHover();
    }

    HighlightHover() {
        if (!this._raycaster || !this._mouse || !this._cubeMesh) return;
        this._raycaster.setFromCamera(this._mouse, this._localCamera);
        let intersects = this._raycaster.intersectObjects(this._cubeMesh.children);
        intersects = intersects.filter((i) => !(i.object instanceof THREE.Line));
        if (intersects.length > 0) {
            let i0 = intersects[0];
            if (i0.object === this._hovered) return;
            let obj = i0.object;
            if (obj instanceof THREE.Mesh) {
                if (this._hovered) {
                    let mat = this._hovered.material as THREE.MeshBasicMaterial;

                    switch (this._hovered.userData.type) {
                        case 'vertex':
                            mat.color.set(this._color);
                            break;
                        case 'face':
                            mat.color.set(this._color);
                            break;
                        case 'edge':
                            mat.color.set(this._color);
                            break;
                    }
                }

                obj.material.color.set(this._highlightColor);
                this._hovered = obj;
            }
        } else if (this._hovered) {
            let mat = this._hovered.material as THREE.MeshBasicMaterial;

            switch (this._hovered.userData.type) {
                case 'vertex':
                    mat.color.set(this._color);
                    break;
                case 'face':
                    mat.color.set(this._color);
                    break;
                case 'edge':
                    mat.color.set(this._color);
                    break;
            }
        }
    }
}
